/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import _ from 'lodash';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import EquityBoxStyles, { themeEquityBoxStyles } from './EquityBox.styles';
interface EquityBoxProps {
    chartTitles: Headings[];
    xLabelsValue: string;
    xGridValues: string;
    yGridValues: string;
    yLabelsValue: string;
    selectionSequence: string;
    dataKey: string;
    transformData?: boolean;
    functionToTransformData?: string;
    typographyClass?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, EquityBoxProps {}

const toArray = (value: any, type: string) => {
    switch (type) {
        case 'string':
            return _.isString(value) ? value.split(',') : [];
        case 'number':
            return _.isString(value) ? value.split(',').map((i) => parseInt(i)) : [];
        default:
            return [];
    }
};

const EquityBox = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        chartTitles,
        xGridValues,
        yGridValues,
        xLabelsValue,
        yLabelsValue,
        selectionSequence,
        dataKey,
        transformData,
        functionToTransformData,
    } = widgetProperties || {};
    const data: any = props.data;
    const i18n = commonHooks.useLocalization();

    const xAxisLabels = JsonQuery(`${xLabelsValue}`, { data }).value || xLabelsValue;
    const yAxisLabels = JsonQuery(`${yLabelsValue}`, { data }).value || yLabelsValue;
    const xAxisLabelsTemplate = xAxisLabels ? i18n.format(xAxisLabels, data) : 'Value,Blend,Growth';
    const yAxisLabelsTemplate = yAxisLabels ? i18n.format(yAxisLabels, data) : 'Large,Med,Small';
    const xLabels = toArray(xAxisLabelsTemplate, 'string');
    const yLabels = toArray(yAxisLabelsTemplate, 'string');
    const xGrids = xGridValues ? toArray(xGridValues, 'number') : [0, 1, 2, 3];
    const yGrids = xGridValues ? toArray(yGridValues, 'number') : [0, 1, 2, 3];
    const selectionSeq = selectionSequence ? toArray(selectionSequence, 'number') : [2, 1, 0];
    const selectedGrid = dataKey ? JsonQuery(`${dataKey}`, { data }).value : 2;
    const theme = data?.themeSelected || props?.theme;

    let isRender = {
        title: true,
        widget: true,
        equityBox: true,
        hideTitlesMsg: '',
        hideWidgetMsg: '',
        hideEquityBoxMsg: '',
    };
    let legend = '';

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToTransformData}`)();
        const _transformedData = transformData && transformFunction({ data });

        if (_transformedData) {
            isRender = { ...isRender, ..._transformedData };
            legend = _transformedData?.legend || '';
        }
    } catch (e) {
        console.log(e);
    }

    return isRender.widget ? (
        <>
            <div
                className={`equity-box-widget ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? EquityBoxStyles : themeEquityBoxStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
            >
                {isRender.title ? (
                    <HeaderWrapper headings={chartTitles} data={data} />
                ) : (
                    <p
                        className={`hide-msg-title`}
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(isRender.hideTitlesMsg, data),
                        }}
                    />
                )}
                {selectedGrid && isRender.equityBox ? (
                    <div className="equity-box-container">
                        <div className="equity-box" data-testid={'equity-box'}>
                            {xGrids.map((i: any) => (
                                <div className={`row row-${i}`} key={i}>
                                    {yGrids.map((j: any) => {
                                        return i === xGrids.length - 1 ? (
                                            <div
                                                className={`box labels-x x-label-${j}`}
                                                key={i + j}
                                            >
                                                <div>{xLabels[j - 1]}</div>
                                            </div>
                                        ) : j === 0 ? (
                                            <div
                                                className={`box labels-y y-label-${i}`}
                                                key={i + j}
                                            >
                                                <div>{yLabels[i]}</div>
                                            </div>
                                        ) : (
                                            <div
                                                className={`box box-${i}-${j} ${
                                                    selectedGrid ===
                                                    parseInt(`${selectionSeq[i]}`) * 3 + j
                                                        ? 'selected'
                                                        : ''
                                                }`}
                                                data-testid={`${
                                                    selectedGrid ===
                                                    parseInt(`${selectionSeq[i]}`) * 3 + j
                                                        ? 'selected-box'
                                                        : ''
                                                }`}
                                                key={i + j}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                        <div className="legend">{legend}</div>
                    </div>
                ) : (
                    <p
                        className={`hide-msg-equity-box`}
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(isRender.hideEquityBoxMsg, data),
                        }}
                    />
                )}
            </div>
        </>
    ) : (
        <p
            className={`hide-msg-widget`}
            dangerouslySetInnerHTML={{
                __html: i18n.format(isRender.hideWidgetMsg, data),
            }}
        />
    );
};

export default EquityBox;
